// A board event and its copies in Google calendars (Fixes 11). Every event is on the board, and is the board's own
// entry; the Google calendars it is also in each hold a separate copy of it, tied to the entry by `g` (the calendar's
// key and the copy's id there). A change made to the entry, from anywhere, is sent to every copy (watchCopies);
// ticking a calendar makes its copy, unticking deletes only that one. The board shows the entry once, with every
// copy's colour (useShownEvents hides the copies it read). Guests are never invited to put an event in another
// address's calendar: a copy is the person's own event in their own calendar.

import { eventOf, type EventData } from "../../../shared/agenda";
import type { Entry, ID } from "../../../shared/types";
import { commit, recordOutside } from "../../state/actions";
import { gcalWrite, useGcal, type WriteEvent, type WriteSpec } from "../../state/gcal";
import { get, glowItems, toast, useStore } from "../../state/store";
import { addEvent } from "./actions";

/** The Google calendars this account writes to, on accounts that work, by key. */
export function writableKeys(): Set<string> {
  const out = new Set<string>();
  for (const a of useGcal.getState().status?.accounts ?? []) if (!a.error) for (const c of a.calendars) if (c.role === "owner" || c.role === "writer") out.add(c.key);
  return out;
}

/** An event's words and times in Google's shape, for a copy; update: a field the event has not got is sent cleared. */
export function bodyOf(ev: EventData, update = false): WriteEvent {
  const b: WriteEvent = { title: ev.title, allDay: ev.allDay, start: ev.start, end: ev.end, tz: ev.allDay ? null : ev.tz };
  if (ev.location || update) b.location = ev.location ?? null;
  if (ev.notes || update) b.notes = ev.notes ?? null;
  if (ev.color || update) b.color = ev.color ?? null;
  if (ev.repeat) b.repeat = ev.repeat;
  return b;
}

/** Whether Google emails an event's guests about the next change sent to its copies (asked first, as for any Google event); once. */
const notifyNext = new Map<ID, boolean>();
export const notifyOnce = (id: ID, send: boolean) => void notifyNext.set(id, send);

/** How many guests an event's copies have, from what the board last read of them: a meeting is the first copy's, which holds its call and guests. */
export function guestsOf(ev: EventData): number {
  for (const [cal, id] of Object.entries(ev.g ?? {}))
    for (const e of Object.values(useGcal.getState().events)) {
      const g = e?.list.find((x) => x.cal === cal && x.id === id);
      if (g) return g.guests ?? 0;
    }
  return 0;
}

/** A copy's delete: a repeating event's goes for every time of it, at once (its copy is the series' own). */
const deleteSpec = (ev: EventData, cal: string, id: string, notify = false): WriteSpec => ({ op: "delete", cal, id, ...(notify ? { notify: true } : {}), ...(ev.repeat ? { scope: "all" as const, series: id } : {}) });

/** What of an event goes to its copies: a change to any of it is sent. */
const sigOf = (ev: EventData) => JSON.stringify([ev.title, ev.allDay, ev.start, ev.end, ev.tz, ev.location ?? null, ev.notes ?? null, ev.color ?? null, ev.repeat ?? null]);

/** Sets or clears (null) the copies an entry records, in one step that is no step of Undo: the copies are Google's, Undo of the words never moves them. */
function setLinks(id: ID, links: Record<string, string | null>) {
  const e = get().entries[id];
  if (!e || e.t !== "event") return;
  const ev = eventOf(e);
  const g: Record<string, string> = { ...(ev.g ?? {}) };
  for (const [k, v] of Object.entries(links)) if (v) g[k] = v;
  else delete g[k];
  // null clears, as the board's cleaner reads it
  commit([{ op: "entry.put", entry: { ...e, data: { ...ev, g: Object.keys(g).length ? g : null } as unknown as Entry["data"] } }], "Calendars", { history: false });
}

/** A copy of an event made in a Google calendar; its id there. meeting: a call and guests (the first copy's alone). */
async function makeCopy(ev: EventData, key: string, meeting: { meet?: boolean; guests?: string[] } = {}): Promise<string | null> {
  const guests = meeting.guests?.length ? meeting.guests : undefined;
  const r = await gcalWrite({ op: "create", cal: key, event: { ...bodyOf(ev), ...(meeting.meet ? { meet: true } : {}), ...(guests ? { guests } : {}) }, ...(guests ? { notify: true } : {}) });
  return r.id ?? r.event?.id ?? null;
}

const failed = (what: string, e: unknown) => toast({ level: "warn", title: what, body: e instanceof Error ? e.message : null });

/** Changes to one event's copies go one after another, each seeing what the one before made (two quick ticks never make a copy twice). */
const turns = new Map<ID, Promise<unknown>>();
function inTurn<T>(id: ID, job: () => Promise<T>): Promise<T> {
  const next = (turns.get(id) ?? Promise.resolve()).catch(() => undefined).then(job);
  turns.set(id, next);
  void next.finally(() => turns.get(id) === next && turns.delete(id)).catch(() => undefined);
  return next;
}

/** Puts an event in one more Google calendar: its copy is made there, and the entry records it. */
export const addCopy = (id: ID, key: string): Promise<boolean> => inTurn(id, () => makeOne(id, key));

/** Takes an event out of one Google calendar: only that copy is deleted, the event stays on the board and in its other calendars. */
export const removeCopy = (id: ID, key: string): Promise<boolean> => inTurn(id, () => dropOne(id, key));

/** Makes an event's copies the Google calendars wanted, of those a list of ticks offers: each missing one made, each other one deleted. */
export function setCopies(id: ID, wanted: string[], among: string[]): Promise<void> {
  return inTurn(id, async () => {
    const e = get().entries[id];
    if (!e || e.t !== "event") return;
    const had = Object.keys(eventOf(e).g ?? {}).filter((k) => among.includes(k));
    for (const k of wanted) if (!had.includes(k)) await makeOne(id, k);
    for (const k of had) if (!wanted.includes(k)) await dropOne(id, k);
  });
}

async function makeOne(id: ID, key: string): Promise<boolean> {
  const e = get().entries[id];
  if (!e || e.t !== "event" || eventOf(e).g?.[key] || !writableKeys().has(key)) return false;
  try {
    const gid = await makeCopy(eventOf(e), key);
    if (!gid) throw new Error("Google did not say where it put the copy.");
    setLinks(id, { [key]: gid });
    // changed on the board while the copy was being made: it has the old words, so they are sent now
    const now = get().entries[id];
    if (now && now.t === "event" && sigOf(eventOf(now)) !== sigOf(eventOf(e))) sendCopies(id, !!eventOf(e).repeat);
    return true;
  } catch (err) {
    failed("Google did not make that copy", err);
    return false;
  }
}

async function dropOne(id: ID, key: string): Promise<boolean> {
  const e = get().entries[id];
  const gid = e && e.t === "event" ? eventOf(e).g?.[key] : undefined;
  if (!gid) return false;
  // unlinked first, so the board never shows the copy it is deleting as an event of its own
  setLinks(id, { [key]: null });
  try {
    await gcalWrite(deleteSpec(eventOf(e!), key, gid));
    return true;
  } catch (err) {
    failed("Google did not delete that copy", err);
    return false;
  }
}

/**
 * An event taken off the board with its copies, and back again: Undo and Redo of a delete, and of a new event. Each copy
 * goes the way a Google delete does, with ten seconds before Google hears of it; back within them it never left, after
 * them it is made again.
 */
function lifecycle(id: ID, first: Entry, notify = false) {
  let saved: Entry = first;
  const pending: Record<string, string | undefined> = {};
  const away = async () => {
    saved = get().entries[id] ?? saved;
    commit([{ op: "entry.del", id }], "Calendars", { history: false });
    for (const [key, gid] of Object.entries(eventOf(saved).g ?? {})) {
      try {
        pending[key] = (await gcalWrite(deleteSpec(eventOf(saved), key, gid, notify && eventOf(saved).g?.[key] === Object.values(eventOf(saved).g ?? {})[0]))).pending;
      } catch (err) {
        failed("Google did not delete a copy", err);
      }
    }
  };
  const back = async () => {
    commit([{ op: "entry.put", entry: saved }], "Calendars", { history: false });
    for (const key of Object.keys(eventOf(saved).g ?? {})) {
      try {
        const held = pending[key];
        const r = held ? await gcalWrite({ op: "undelete", cal: key, pending: held }) : { gone: true };
        if (r.gone) {
          const made = await makeCopy(eventOf(saved), key);
          if (made) setLinks(id, { [key]: made });
        }
        pending[key] = undefined;
      } catch (err) {
        failed("Google did not bring a copy back", err);
      }
    }
  };
  return { away, back };
}

/** An event deleted from the board, its copies too; the toast's Undo (and Ctrl Z) bring all of it back. */
export function deleteWithCopies(id: ID, notify = false) {
  const e = get().entries[id];
  if (!e || e.t !== "event") return;
  const life = lifecycle(id, e, notify);
  void life.away();
  const takeBack = recordOutside("Delete event", { undo: () => void life.back(), redo: () => void life.away() });
  toast({ level: "info", title: "Deleted", body: "Its copies leave Google in 10 seconds.", ms: 10_000, action: { label: "Undo", run: takeBack } });
}

/**
 * A new event: on the board always, and a copy in each Google calendar given (those this account writes to), the
 * first with the call and guests. One step on Undo, which takes the event and its copies away.
 */
export async function createIn(boardId: ID, cals: string[], data: Omit<EventData, "tz"> & { tz?: string | null }, meeting: { meet?: boolean; guests?: string[] } = {}): Promise<void> {
  const ok = writableKeys();
  const keys = cals.filter((k) => ok.has(k));
  if (!keys.length) return void addEvent(boardId, data);
  const entry = addEvent(boardId, data, false);
  if (!entry) return;
  const ev = eventOf(entry);
  const links: Record<string, string> = {};
  for (const [i, key] of keys.entries()) {
    try {
      const gid = await makeCopy(ev, key, i === 0 ? meeting : {});
      if (gid) links[key] = gid;
      else throw new Error("Google did not say where it put the copy.");
    } catch (err) {
      failed("Google did not make that event", err);
    }
  }
  if (Object.keys(links).length) setLinks(entry.id, links);
  const now = get().entries[entry.id] ?? entry;
  const life = lifecycle(entry.id, now);
  recordOutside("Add event", { undo: () => void life.away(), redo: () => void life.back() });
}

/** A Google event with no copy tie, made an event of the board that holds it (the tie to it recorded), so it can go in other calendars too. */
export function adopt(boardId: ID, g: { cal: string; id: string; ev: EventData; color: EventData["color"] | null }): Entry | null {
  const entry = addEvent(boardId, { ...g.ev, ...(g.color ? { color: g.color } : {}), g: { [g.cal]: g.id } }, false);
  if (entry) setTimeout(() => glowItems([entry.id]), 80);
  return entry;
}

// ---------------------------------------------------------------------------
// a change on the board reaches the copies

const seen = new Map<ID, string>();
const sending = new Map<ID, Promise<unknown>>();
const later = new Map<ID, ReturnType<typeof setTimeout>>();
let watching = false;

/** Sends the entry as it is now to each of its copies, one send after another for one entry. */
function sendCopies(id: ID, hadRepeat: boolean) {
  const run = async () => {
    const e = get().entries[id];
    if (!e || e.t !== "event" || get().guest) return;
    const ev = eventOf(e);
    const ok = writableKeys();
    const notify = notifyNext.get(id) ?? false;
    notifyNext.delete(id);
    for (const [key, gid] of Object.entries(ev.g ?? {})) {
      if (!ok.has(key)) continue;
      try {
        // a repeat taken off is sent cleared; a copy with none is sent none
        const body = bodyOf(ev, true);
        if (!ev.repeat && hadRepeat) body.repeat = null;
        await gcalWrite({ op: "update", cal: key, id: gid, event: body, ...(notify && gid === Object.values(ev.g ?? {})[0] ? { notify: true } : {}) });
      } catch (err) {
        failed("Google did not take that change", err);
      }
    }
  };
  const next = (sending.get(id) ?? Promise.resolve()).catch(() => undefined).then(run);
  sending.set(id, next);
  void next.finally(() => sending.get(id) === next && sending.delete(id)).catch(() => undefined);
}

/** From now on, a change to an event that has copies (a drag, a rename, Undo, an agent's edit) is sent to them; started once. */
export function watchCopies() {
  if (watching) return;
  watching = true;
  const repeats = new Map<ID, boolean>();
  // what is there already is the starting point: only a change after this is sent
  for (const e of Object.values(get().entries)) if (e.t === "event" && eventOf(e).g) (seen.set(e.id, sigOf(eventOf(e))), repeats.set(e.id, !!eventOf(e).repeat));
  useStore.subscribe((s, prev) => {
    if (s.entries === prev.entries) return;
    for (const e of Object.values(s.entries)) {
      if (e.t !== "event") continue;
      const ev = eventOf(e);
      if (!ev.g || !Object.keys(ev.g).length) {
        seen.delete(e.id);
        continue;
      }
      const sig = sigOf(ev);
      const was = seen.get(e.id);
      seen.set(e.id, sig);
      if (was === undefined || was === sig) {
        repeats.set(e.id, !!ev.repeat);
        continue;
      }
      // a burst of changes (a drag's steps, quick typing) is one send of the last
      const hadRepeat = repeats.get(e.id) ?? false;
      repeats.set(e.id, !!ev.repeat);
      clearTimeout(later.get(e.id));
      later.set(
        e.id,
        setTimeout(() => {
          later.delete(e.id);
          sendCopies(e.id, hadRepeat);
        }, 350),
      );
    }
  });
}
